import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { useFormMutation } from "@sinch/hooks/useFormMutation";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { useEffect } from "react";
import { MessageFormFields, messageSchema } from "./MessageForm/formSchema";
import { resolveChannelMessage } from "./MessageForm/resolveChannelMessage";
import type { Placeholders } from "./MessageForm/MessageForm";
import { useRecipientsChannelsAvailability } from "./MessageForm/useRecipientsChannelsAvailability";
import { isNotNilOrEmpty } from "ramda-adjunct";
import { SendMessage } from "@sinch/components/application/Messages/MessageForm/queries.graphql";
import {
  SendMessageMutation,
  SendMessageMutationVariables,
} from "@sinch/components/application/Messages/MessageForm/queries.types";

/**
 * Props for the MessageDialog component.
 */
interface UseMessageDialogProps {
  /**
   * An array of initial recipients (worker ids) for the message dialog.
   */
  initRecipients: string[];

  /**
   * An optional related object associated with the message dialog.
   */
  relatedObject?: string;

  /**
   * An optional ID of the related object associated with the message dialog.
   */
  relatedObjectId?: string;

  /**
   * Callback function to be called upon successful completion of the send of message.
   */
  onSuccess: () => void;
  placeholders?: Partial<Record<NotificationChannel, Placeholders>>;

  defaultOpened?: NotificationChannel;
}

interface UseMessageReturn {
  form: ReturnType<typeof useMuiForm<MessageFormFields>>;
  handleSubmit: () => Promise<void>;
  sending: boolean;
}

export const useMessageSend = ({
  initRecipients,
  relatedObject,
  relatedObjectId,
  onSuccess,
  placeholders = {},
  defaultOpened,
}: UseMessageDialogProps): UseMessageReturn => {
  const recipientsAvailability = useRecipientsChannelsAvailability();

  // create instace of form, with or without data
  const form = useMuiForm<MessageFormFields>({
    formOptions: {
      mode: "onChange",
      reValidateMode: "onChange",
    },
    schema: messageSchema({ initRecipients, recipientsAvailability, placeholders, defaultOpened }),
  });

  const { handleSubmit, loading } = useFormMutation<
    SendMessageMutation,
    SendMessageMutationVariables,
    MessageFormFields
  >({
    mutation: {
      mutate: SendMessage,
    },
    form,
    variableMapper: ({ message, recipients, userGroups, subject, textMessage, pushMessage, channels }) => {
      const onlyAvailableUsers = recipientsAvailability.getAvailableRecipients(recipients, channels);

      return {
        message: {
          channels: channels,
          message: resolveChannelMessage(channels, { message, textMessage, pushMessage }),
          subject: subject,
          userIds: onlyAvailableUsers,
          userGroups: userGroups,
          relatedObject,
          relatedObjectId,
        },
      };
    },
  });

  useEffect(() => {
    if (isNotNilOrEmpty(initRecipients)) {
      form.reset(messageSchema({ initRecipients, defaultOpened }).getDefault());
    }
  }, [initRecipients]);

  const handleSend = async () => {
    try {
      const response = await handleSubmit();
      if (response?.sendMessage.result) {
        onSuccess();
      }
    } catch (e) {
      console.error(e);
      return;
    }
  };

  return {
    form,
    handleSubmit: handleSend,
    sending: loading,
  };
};
